import type { RevitOperationProgressSnapshot } from './operationProgressSnapshotMapper';

export interface SnapshotProgressVisibility {
  percent: number | null | undefined;
  etaMs: number | null | undefined;
}

type SnapshotProgressSource = Pick<
  RevitOperationProgressSnapshot,
  'state' | 'percent' | 'total' | 'etaMs'
>;

/**
 * Single source of truth for whether a polled snapshot may drive a determinate
 * progress bar. Used by both the proto snapshot mapper and the UI model mapper
 * so the two layers can never drift apart.
 *
 * `waiting_for_revit` means the operation is still queued behind the Revit
 * external event, so no work has happened and the bar must stay indeterminate.
 * `waiting_for_user_input` scopes wrap long foreground Revit calls (EditFamily,
 * transaction commit, LoadFamily) that dominate wall time in family parameter
 * operations; progress reported before them stays meaningful, so the bar keeps
 * its percent and ETA instead of collapsing to a spinner for most of the run.
 */
export function resolveSnapshotProgressVisibility(
  snapshot: SnapshotProgressSource
): SnapshotProgressVisibility {
  const etaMs = (snapshot.etaMs ?? 0) > 0 ? snapshot.etaMs : undefined;

  if (snapshot.state === 'waiting_for_user_input') {
    return {
      percent: (snapshot.total ?? 0) > 0 ? snapshot.percent ?? null : null,
      etaMs,
    };
  }

  if (snapshot.state === 'waiting_for_revit') {
    return { percent: null, etaMs: undefined };
  }

  return { percent: snapshot.percent, etaMs };
}
